import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';

// Theme Colors
const themeColors = {
  primary: '#31C48D', // Green
  secondary: '#007BFF', // Blue
  text: '#4D4D4D', // Dark Grey
  hover: '#F1F1F1', // Hover light grey
  active: '#007BFF', // Active tab highlight
};

// TabBar Component
const TabBar = () => {
  const navigate = useNavigate();

  // State to control visibility
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll detection logic
  const handleScroll = () => {
    if (window.scrollY > lastScrollY) {
      setVisible(false); // Hide on scroll down
    } else {
      setVisible(true); // Show on scroll up
    }
    setLastScrollY(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll); // Attach scroll listener
    return () => window.removeEventListener('scroll', handleScroll); // Cleanup listener
  }, [lastScrollY]);

  // Tab data with routes
  const tabs = [
    { label: 'Lab Tests', icon: <ScienceIcon fontSize="small" />, route: '/lab-tests' },
    { label: 'Medicines', icon: <MedicationIcon fontSize="small" />, route: '/medicines' },
    { label: 'Pharmacies', icon: <LocalPharmacyIcon fontSize="small" />, route: '/pharmacies' },
    { label: 'Doctors', icon: <LocalHospitalIcon fontSize="small" />, route: '/doctors' },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: themeColors.white, // Solid white background
        padding: '4px 0', // Smaller height
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)', // Subtle shadow
        borderBottom: '1px solid #E0E0E0', // Border for separation
        position: 'sticky',
        top: visible ? '64px' : '-60px', // Instantly hide or show
        zIndex: 1100, // High z-index
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: '4px 8px', // Smaller padding
        }}
      >
        {tabs.map((tab, index) => (
          <Box
            key={index}
            onClick={() => navigate(tab.route)} // Navigate on click
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px', // Gap between icon and label
              cursor: 'pointer',
              padding: '4px 8px', // Padding for hover effect
              borderRadius: '8px',
              '&:hover': { backgroundColor: themeColors.hover }, // Hover effect
              '&:active': { color: themeColors.active },
            }}
          >
            <IconButton
              sx={{
                padding: '4px', // Smaller icon padding
                color: themeColors.text,
                '&:hover': { color: themeColors.active },
              }}
            >
              {tab.icon}
            </IconButton>
            <Typography
              variant="body2" // Smaller font size
              sx={{
                fontWeight: 500,
                color: themeColors.text,
                '&:hover': { color: themeColors.active },
              }}
            >
              {tab.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TabBar;